/**
 * ManifestoGate — blocking modal that gates the learner portal until the
 * UniVicoustic Founding Team Manifesto has been read end-to-end and
 * acknowledged. Acknowledgements expire after 90 days (server-side rule).
 *
 * Flow:
 *   1. Mount → GET /api/gates/manifesto/status
 *   2. If `acknowledged === false` → render full-screen overlay with the
 *      PDFViewer inside. The "I confirm I have read and understood" button
 *      stays disabled until PDFViewer fires `onReadEnd` (user scrolled past
 *      the last page).
 *   3. On confirm → POST /api/gates/manifesto/acknowledge, then unmount.
 *
 * No close / dismiss / X-button by design. The brief said:
 * "he/she should be able to proceed only after they have read through".
 */
import React, { useEffect, useState } from "react";
import { CheckCircle2, ShieldAlert } from "lucide-react";

import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { PDFViewer } from "./PDFViewer";

const ACK_TEXT =
  "I confirm that I have read and understood \u201CBelieve Before We Sell\u201D — the " +
  "UniVicoustic Founders' Manifesto — in its entirety, and I commit to " +
  "upholding its principles in every aspect of my work with the organisation.";

// Set after a confirmed read; cleared at every login and on browser close so
// the manifesto is read and confirmed again on every login.
export const MANIFESTO_SESSION_KEY = "uvc_manifesto_ack_session";

export function ManifestoGate({ onAcknowledged }) {
  const [open, setOpen] = useState(false);
  const [readEnd, setReadEnd] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Force-reopen path — when the user clicks the "Founder Manifesto"
    // tile we navigate with `?reread=manifesto` so the gate opens even
    // mid-session (and still records a fresh acknowledgement on confirm).
    const forceReread = new URLSearchParams(window.location.search).get("reread") === "manifesto";
    if (forceReread) {
      setOpen(true);
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete("reread");
        window.history.replaceState({}, "", u.toString());
      } catch (_) { /* noop */ }
      return;
    }
    // The manifesto must be read and confirmed on EVERY login: the session
    // key is set only after confirming, and cleared on each fresh login
    // (see completeLogin in Login.jsx) and whenever the browser is closed.
    if (!sessionStorage.getItem(MANIFESTO_SESSION_KEY)) setOpen(true);
    else onAcknowledged?.();
  }, [onAcknowledged]);

  // Lock body scroll while the gate is open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const submit = async () => {
    if (submitting || !readEnd || !confirmed) return;
    try {
      setSubmitting(true);
      setError(null);
      await api.post("/gates/manifesto/acknowledge", { confirmed: true });
      sessionStorage.setItem(MANIFESTO_SESSION_KEY, new Date().toISOString());
      setOpen(false);
      onAcknowledged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not submit acknowledgement. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] bg-[#222222]/95 backdrop-blur-sm flex items-stretch justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="manifesto-title"
      data-testid="manifesto-gate"
    >
      <div className="w-full max-w-4xl m-4 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-[#222222] to-[#1a1a1a] text-white">
          <div className="flex items-center gap-3">
            <ShieldAlert className="text-[#E53935]" size={22} />
            <h2 id="manifesto-title" className="text-lg font-semibold tracking-tight">
              &ldquo;Believe Before We Sell&rdquo; — required reading
            </h2>
          </div>
          <p className="text-sm text-slate-300 mt-1">
            The <span className="text-[#E53935] font-semibold">Founders&rsquo; Manifesto</span> — a
            message from the Managing Director. Please read end-to-end; the confirmation button
            activates once you have scrolled through the entire document.
          </p>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-slate-200">
          <div
            className="h-1 bg-[#E53935] transition-all duration-200"
            style={{ width: `${progress}%` }}
            data-testid="manifesto-progress"
          />
        </div>

        {/* PDF body */}
        <div className="flex-1 min-h-0">
          <PDFViewer
            src="/api/gates/believe.pdf"
            onProgress={setProgress}
            onReadEnd={() => setReadEnd(true)}
            ariaLabel="Believe Before We Sell — UniVicoustic Founders' Manifesto"
          />
        </div>

        {/* Confirm row */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="manifesto-confirm-row">
            <Checkbox
              id="manifesto-confirm"
              checked={confirmed}
              onCheckedChange={(v) => setConfirmed(!!v)}
              disabled={!readEnd}
              data-testid="manifesto-confirm-checkbox"
              className="mt-0.5"
            />
            <span className="text-sm text-slate-800 leading-relaxed select-none">
              {ACK_TEXT}
            </span>
          </label>
          {error && (
            <div className="mt-2 text-xs text-rose-700" data-testid="manifesto-error">{error}</div>
          )}
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500" data-testid="manifesto-progress-text">
              {readEnd ? (
                <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                  <CheckCircle2 size={14}/> You&rsquo;ve read the entire document.
                </span>
              ) : (
                <>Progress: <strong className="text-slate-700">{progress}%</strong> — scroll to the last page to enable confirmation.</>
              )}
            </div>
            <Button
              onClick={submit}
              disabled={!readEnd || !confirmed || submitting}
              data-testid="manifesto-confirm-button"
              className="bg-[#E53935] hover:bg-[#c62828] text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting…" : "Confirm & proceed"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManifestoGate;
